import React, { useState, useEffect, useRef } from 'react';
import { resolveImageUrl } from '../utils/imageUrl';

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholderColor = 'bg-gray-800',
  fallbackSrc = 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const imgRef = useRef(null);

  // Resolve the image URL to handle relative paths and old domains
  const resolvedSrc = resolveImageUrl(src);

  // Update currentSrc when resolvedSrc changes or on error
  useEffect(() => {
    if (hasError) {
      setCurrentSrc(fallbackSrc);
    } else {
      setCurrentSrc(resolvedSrc);
    }
  }, [resolvedSrc, hasError, fallbackSrc]);

  useEffect(() => {
    // Check if IntersectionObserver is available
    if (!('IntersectionObserver' in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px',
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    if (!hasError) {
      // Only switch to fallback once
      setHasError(true);
      setIsLoaded(false); // Reset to show loading while fallback loads
    } else {
      // Fallback also failed, just show it anyway
      setIsLoaded(true);
    }
  };

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`}>
      {/* Placeholder - show only while loading */}
      {!isLoaded && (
        <div 
          className={`absolute inset-0 ${placeholderColor} flex items-center justify-center`}
        >
          <div className="w-8 h-8 border-2 border-gray-600 border-t-orange-500 rounded-full animate-spin" />
        </div>
      )}

      {/* Actual Image */}
      {isInView && currentSrc && (
        <img
          src={currentSrc}
          alt={alt || 'Blog image'}
          onLoad={handleLoad}
          onError={handleError}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          {...props}
        />
      )}
    </div>
  );
};

export default LazyImage;
